import { Flex, Text, Heading, Center, useMediaQuery, Link, Image, VStack } from '@chakra-ui/react'
import {
  ConnectedFormGroup,
  ConnectedImageUploader,
  ConnectedPasswordGroup
} from 'components/FormElements'
import { PHONE_NUMBER_REGEX } from '../../../constants'
import { useAuthContext } from 'context/AuthProvider'
import { Form, Formik, FormikProps } from 'formik'
import * as React from 'react'
import { VscMail } from 'react-icons/vsc'
import { useHistory } from 'react-router-dom'
import * as Yup from 'yup'
import { PrimaryButton } from 'components/Buttons'
import { FaIdCard, FaPhone, FaUser } from 'react-icons/fa'
import ErrorIndicator from 'components/utils/ErrorIndicator'
import { formatError } from 'utils'
import { images } from 'theme'
import { useTranslation } from 'react-i18next'

type RegisterAgentProps = {}

const RegisterAgentFormValidation = Yup.object().shape({
  firstName: Yup.string().required('A firstName is required'),
  lastName: Yup.string().required('A lastName is required'),
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('An email address is required'),
  agentNumber: Yup.string(),
  password: Yup.string()
    .min(8, 'Password has to be longer than 8 characters')
    .required('A password is required'),
  confirmPassword: Yup.string()
    .required('Confirm your password')
    .oneOf([Yup.ref('password')], 'Password does not match'),
  cellNumber: Yup.string()
    .matches(PHONE_NUMBER_REGEX, 'Please enter a valid cell number')
    .required('A cell number address is required'),
  profilePicture: Yup.mixed()
})

export type AgentRegisterValues = {
  firstName: string
  lastName: string
  agentNumber: string
  email: string
  password: string
  confirmPassword: string
  cellNumber: string
  profilePicture: any
}

const RegisterAgent: React.FC<RegisterAgentProps> = () => {
  const { user, logout, register } = useAuthContext()

  const history = useHistory()
  const { t } = useTranslation()
  const [isTabletOrMobile] = useMediaQuery('(max-width: 40em)')
  React.useEffect(() => {
    if (user?.confirmed) {
      history.push('/auth')
    } else if (user) {
      history.push('/confirm-email')
      logout && logout()
    }
    // eslint-disable-next-line
  }, [user])

  return (
    <>
      <Flex flex={1} flexDirection={isTabletOrMobile ? 'column' : 'row'}>
        <Flex width="100%" direction="column" flex={1}>
          <VStack my={8} alignItems="flex-start" spacing={0}>
            <Heading as="h3" fontWeight="extrabold" textAlign="left">
              {t('signup.agent.title')}
            </Heading>
            <Text fontSize="xs" color="gray.500">
              {t('signup.agent.sub_title')}
            </Text>
          </VStack>

          <Formik
            validationSchema={RegisterAgentFormValidation}
            initialValues={{
              cellNumber: '',
              email: '',
              password: '',
              agentNumber: '',
              confirmPassword: '',
              firstName: '',
              lastName: '',
              profilePicture: null
            }}
            onSubmit={async (
              { firstName, lastName, agentNumber, email, password, cellNumber, profilePicture },
              { setStatus, setSubmitting }
            ) => {
              setStatus(null)
              try {
                setSubmitting(true)

                if (register) {
                  const fullName = `${firstName} ${lastName} - ${agentNumber}`

                  await register(fullName, firstName, lastName, email, password, cellNumber, {
                    role: 'agent',
                    payload: {
                      agentNumber,
                      profilePicture
                    }
                  })
                }

                setSubmitting(false)
              } catch (error: any) {
                setStatus(formatError(error))
              }
            }}
          >
            {({ isSubmitting, status }: FormikProps<AgentRegisterValues>) => (
              <Form style={{ width: '100%' }} autoComplete="off">
                <Flex w="100%" direction={isTabletOrMobile ? 'column' : 'row'}>
                  <ConnectedFormGroup
                    name="firstName"
                    leftIcon={FaUser}
                    mr={isTabletOrMobile ? 0 : 2}
                    label={t('signup.agent.first_name_label')}
                    width="100%"
                    placeholder="ex: John"
                  />
                  <ConnectedFormGroup
                    name="lastName"
                    leftIcon={FaUser}
                    label={t('signup.agent.last_name_label')}
                    width="100%"
                    placeholder="ex: Deo"
                  />
                </Flex>
                <Flex w="100%" direction={isTabletOrMobile ? 'column' : 'row'}>
                  <ConnectedFormGroup
                    name="email"
                    leftIcon={VscMail}
                    label={t('signup.agent.email_label')}
                    mr={isTabletOrMobile ? 0 : 2}
                    width="100%"
                    placeholder="ex: dorny@nsoko.io"
                    type="text"
                  />
                  <ConnectedFormGroup
                    name="cellNumber"
                    leftIcon={FaPhone}
                    width="100%"
                    maxLength={10}
                    label={t('signup.agent.phone_number_label')}
                    placeholder="ex: 08198765432"
                  />
                </Flex>
                <ConnectedFormGroup
                  name="agentNumber"
                  label={t('signup.agent.agent_number_label')}
                  leftIcon={FaIdCard}
                  placeholder="1234567890"
                  maxLength={10}
                />

                <Flex mt={4} direction={isTabletOrMobile ? 'column' : 'row'}>
                  <ConnectedPasswordGroup
                    name="password"
                    isRequired
                    width="100%"
                    mr={isTabletOrMobile ? 0 : 2}
                    label={t('signup.agent.password_label')}
                    placeholder="Password"
                  />
                  <ConnectedPasswordGroup
                    name="confirmPassword"
                    label={t('signup.agent.password_confirm_label')}
                    width="100%"
                    isRequired
                    placeholder="Confirm Password"
                  />
                </Flex>

                <ConnectedImageUploader name="profilePicture" label="Admin's Photo" />

                {status && <ErrorIndicator status={status} />}
                <PrimaryButton
                  my={6}
                  width="100%"
                  colorScheme="orange"
                  size="md"
                  rounded="lg"
                  type="submit"
                  isLoading={isSubmitting}
                >
                  {t('signup.agent.signup_btn')}
                </PrimaryButton>
                <Flex my={8} align="center" justify="center">
                  <Text fontWeight="semibold" fontSize="sm">
                    {t('signup.client.already_have_account')}{' '}
                    <Link color="blue.500" onClick={() => history.push('/login')}>
                      {t('signup.client.signin')}
                    </Link>{' '}
                  </Text>
                </Flex>
              </Form>
            )}
          </Formik>
        </Flex>
        <Center w="100%" flex={1.2} height="100%" flexDirection="column">
          <Image src={images.agentIlu} w="60%" />
        </Center>
      </Flex>
    </>
  )
}

export default RegisterAgent
